"use client";
export const dynamic = "force-dynamic";

import Link from "next/link";
import { motion } from "framer-motion";
import {
  Zap,
  Hash,
  MessageCircle,
  Image as ImageIcon,
  Workflow,
  Filter,
  GitBranch,
  Clock,
  Bell,
  Target,
  BarChart3,
  Bot,
  Eye,
  ArrowLeft,
  PlayCircle,
  MousePointer2,
  Repeat,
  ShieldCheck,
  Sparkles,
} from "lucide-react";
import SiteHeader from "@/components/landing/SiteHeader";
import SiteFooter from "@/components/landing/SiteFooter";
import {
  SolutionShell,
  Orbs,
  GridBg,
  Section,
  SectionHeader,
  GiantStats,
  LineFeatures,
  SplitFeature,
  StepsTimeline,
  CompareSplit,
  BigQuote,
  MiniTestimonials,
  FAQ,
  FinalCTA,
  HeroBadge,
  HeroTitle,
  PricingStrip,
} from "@/components/landing/solutions/SolutionSections";

function Page() {
  return (
    <SolutionShell>
      <SiteHeader hashMode={false} />

      <section className="relative min-h-screen flex items-center pt-32 pb-20 overflow-hidden">
        <GridBg />
        <Orbs className="inset-0" />
        <div className="container mx-auto px-6 max-w-7xl relative grid lg:grid-cols-[1fr_1.05fr] gap-14 items-center">
          <div className="text-center lg:text-right">
            <HeroBadge>No-code Flow · بدون یک خط کد</HeroBadge>
            <HeroTitle pre="پاسخ خودکار،" gradient="بدون یک خط کد" />
            <p className="text-base md:text-lg text-muted-foreground max-w-xl mx-auto lg:mx-0 mb-10 leading-relaxed">
              تریگر روی کلمه، کامنت، هشتگ، DM یا استوری؛ فلوهای no-code با شرط، تأخیر و A/B تست.
            </p>
            <div className="flex flex-col sm:flex-row items-center lg:items-start justify-center lg:justify-start gap-4 mb-10">
              <Link
                href="/auth"
                className="group w-full sm:w-auto px-8 py-4 rounded-full bg-foreground text-background font-bold flex items-center justify-center gap-2 shadow-2xl shadow-primary-main/20"
              >
                ساخت فلو <ArrowLeft size={18} />
              </Link>
              <Link
                href="/dashboard/auto-reply"
                className="group flex items-center gap-3 text-foreground/75 hover:text-foreground"
              >
                <span className="w-12 h-12 rounded-full bg-foreground/5 border border-border grid place-items-center">
                  <PlayCircle size={18} />
                </span>
                <span className="text-sm font-medium">دموی فلو</span>
              </Link>
            </div>
            <div className="flex flex-wrap items-center justify-center lg:justify-start gap-x-7 gap-y-3 text-xs text-muted-foreground/80">
              <span className="flex items-center gap-1.5">
                <ShieldCheck size={13} className="text-primary-main" /> ایمن و مطابق Meta
              </span>
              <span className="flex items-center gap-1.5">
                <Zap size={13} className="text-secondary-main" /> &lt;۵s واکنش
              </span>
            </div>
          </div>
          <FlowMock />
        </div>
      </section>

      <GiantStats
        items={[
          { value: "+۴۲٪", label: "نرخ تبدیل کامنت" },
          { value: "۲۰+", label: "نوع تریگر" },
          { value: "<۵s", label: "زمان واکنش" },
          { value: "A/B", label: "تست بومی" },
        ]}
      />

      <Section>
        <SectionHeader eyebrow="ابزارها" title="فلو بساز، تست بگیر، رشد کن" />
        <LineFeatures
          items={[
            {
              icon: Hash,
              title: "تریگر کامنت / هشتگ / منشن",
              desc: "روی هر کلمه، هشتگ یا منشن واکنش خودکار.",
            },
            { icon: MessageCircle, title: "تریگر دایرکت", desc: "پاسخ به DM با AI یا تمپلیت." },
            { icon: ImageIcon, title: "تریگر استوری", desc: "Reply / Reaction به استوری." },
            { icon: GitBranch, title: "شرط و انشعاب", desc: "If/Else بر اساس فیلد، تگ، زبان." },
            { icon: Clock, title: "تأخیر و زمان‌بندی", desc: "بعد از X دقیقه، یا در ساعت کاری." },
            { icon: Workflow, title: "دنبالهٔ پیام (Drip)", desc: "کمپین چند مرحله‌ای زنجیره‌ای." },
            {
              icon: Target,
              title: "A/B تست بومی",
              desc: "دو ورژن پاسخ، برنده خودکار اعمال می‌شه.",
            },
            { icon: Filter, title: "فیلتر سگمنت", desc: "فقط VIP، فقط مشتری جدید و ..." },
          ]}
        />
      </Section>

      <Section className="border-t border-border">
        <SplitFeature
          eyebrow="Flow Builder"
          title={
            <>
              Drag & Drop، <span className="brand-gradient-text">بدون کد</span>
            </>
          }
          desc="هر گام رو با یک کلیک اضافه کن. شرط، تأخیر، AI، پاس به اپراتور — همه آماده."
          bullets={["۲۰+ نوع گام آماده", "Sandbox برای تست", "نسخه‌بندی فلو"]}
          slot={<FlowMock compact />}
        />
      </Section>

      <Section className="border-t border-border">
        <SplitFeature
          reverse
          eyebrow="Funnel و A/B"
          title={
            <>
              ببین مشتری <span className="brand-gradient-text">کجا</span> دراپ می‌کنه
            </>
          }
          desc="هر گام فلو، آمار ورودی و خروجی داره. A/B تست برنده رو خودکار انتخاب می‌کنه."
          bullets={["Funnel زنده هر فلو", "A/B دو نسخه پاسخ", "Heatmap کلیک"]}
          slot={<FunnelMock />}
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="در ۴ گام" title="از ایده تا فلو زنده" />
        <StepsTimeline
          steps={[
            { title: "تریگر را انتخاب کن", desc: "کامنت، هشتگ، DM، استوری، فرم." },
            { title: "گام‌ها را بساز", desc: "پاسخ، تأخیر، شرط، AI." },
            { title: "تست در Sandbox", desc: "قبل از انتشار، روی پیج تست." },
            { title: "Live و بهینه‌سازی", desc: "Funnel و A/B تست." },
          ]}
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="مقایسه" title="فلوبیلدر ترندی در برابر بقیه" accent="secondary" />
        <CompareSplit
          ours={[
            "Drag & Drop کامل",
            "تریگر استوری/کامنت/DM",
            "A/B تست بومی",
            "AI step داخل فلو",
            "گزارش Funnel زنده",
          ]}
          theirs={[
            "فرم‌های پیچیده",
            "فقط DM پشتیبانی می‌شه",
            "بدون A/B",
            "بدون اتصال AI",
            "گزارش‌های ضعیف",
          ]}
        />
      </Section>

      <Section>
        <BigQuote
          quote="هشتگ کمپین = دایرکت اتومات. فروش ۲ برابر شد و من دیگه شب‌ها استرس پاسخ‌گویی ندارم."
          name="نازی فرهمند"
          role="بوتیک Liva"
        />
      </Section>

      <Section className="border-t border-border">
        <SectionHeader eyebrow="مشتریان" title="کسب‌وکارهایی که فلو ساختند" />
        <MiniTestimonials
          items={[
            { name: "محمد سپهر", role: "آکادمی موسیقی", quote: "فلوی ثبت‌نام دوره خودکار شد." },
            {
              name: "Sara T.",
              role: "Travel Agency",
              quote: "Story replies turn into bookings now.",
            },
            { name: "حسین م.", role: "Cafe Brand", quote: "تریگر کامنت برامون سفارش آورد." },
          ]}
        />
      </Section>

      <Section>
        <SectionHeader eyebrow="پرسش‌ها" title="سؤالات متداول" />
        <FAQ
          items={[
            { q: "چند فلو همزمان می‌تونم داشته باشم؟", a: "در پلن استارتر ۵، Pro نامحدود." },
            { q: "آیا فلو روی واتس‌اپ هم کار می‌کنه؟", a: "بله، روی همه‌ی کانال‌های متصل." },
            { q: "گزارش لحظه‌ای داره؟", a: "بله، ورود/خروج هر گام رو زنده ببین." },
          ]}
        />
      </Section>

      <PricingStrip />
      <FinalCTA
        title={
          <>
            اولین فلوی <span className="brand-gradient-text">no-code</span> رو امروز بساز
          </>
        }
        desc="کمتر از ۱۰ دقیقه تا اولین تبدیل خودکار."
      />
      <SiteFooter />
    </SolutionShell>
  );
}

function FlowMock({ compact = false }: { compact?: boolean }) {
  const nodes = [
    { i: MousePointer2, t: "تریگر", d: "کامنت با هشتگ #تخفیف" },
    { i: GitBranch, t: "شرط", d: "اگر فالووره؟" },
    { i: Bot, t: "ترندی AI", d: "پاسخ شخصی‌سازی‌شده" },
    { i: Clock, t: "تأخیر", d: "بعد از ۲ ساعت" },
    { i: MessageCircle, t: "دایرکت", d: "ارسال کد تخفیف" },
  ];
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="relative rounded-3xl border border-border bg-background p-5 shadow-2xl"
    >
      {!compact && (
        <div className="absolute -inset-6 rounded-[3rem] bg-gradient-to-br from-primary-main/30 to-secondary-main/30 blur-3xl opacity-50 -z-10" />
      )}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2 text-sm font-black text-foreground">
          <Workflow size={16} className="text-secondary-main" /> Flow Builder
        </div>
        <div className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-primary-main animate-pulse" />
          <span className="text-[10px] text-primary-main font-black">LIVE</span>
        </div>
      </div>
      <div className="space-y-2">
        {nodes.map((n, i) => (
          <div key={n.t}>
            <div className="rounded-xl border border-border bg-foreground/[0.04] p-3 flex items-center gap-3">
              <div className="w-9 h-9 rounded-lg bg-secondary-main/15 text-secondary-main grid place-items-center">
                <n.i size={16} />
              </div>
              <div className="flex-1">
                <div className="text-xs font-black text-foreground">{n.t}</div>
                <div className="text-[11px] text-muted-foreground">{n.d}</div>
              </div>
              <div className="text-[10px] text-muted-foreground/80">
                {i + 1}/{nodes.length}
              </div>
            </div>
            {i < nodes.length - 1 && <div className="w-px h-3 bg-foreground/10 mx-auto" />}
          </div>
        ))}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-2 pt-3 border-t border-border">
        <div className="text-center">
          <div className="text-sm font-black text-secondary-main">۲.۴k</div>
          <div className="text-[10px] text-muted-foreground">ورودی</div>
        </div>
        <div className="text-center">
          <div className="text-sm font-black text-primary-main">۱.۸k</div>
          <div className="text-[10px] text-muted-foreground">DM ارسالی</div>
        </div>
        <div className="text-center">
          <div className="text-sm font-black text-foreground">۴۲٪</div>
          <div className="text-[10px] text-muted-foreground">تبدیل</div>
        </div>
      </div>
    </motion.div>
  );
}

function FunnelMock() {
  const stages = [
    { l: "ورود", v: 100, n: "۲٬۴۰۰" },
    { l: "شرط", v: 78, n: "۱٬۸۷۲" },
    { l: "AI", v: 65, n: "۱٬۵۶۰" },
    { l: "DM", v: 48, n: "۱٬۱۵۲" },
    { l: "خرید", v: 32, n: "۷۶۸" },
  ];
  return (
    <div className="rounded-3xl border border-border bg-foreground/[0.04] p-6">
      <div className="flex items-center gap-2 text-sm font-black text-foreground mb-5">
        <BarChart3 size={16} className="text-secondary-main" /> Funnel فلو
      </div>
      <div className="space-y-3">
        {stages.map((s, i) => (
          <div key={s.l}>
            <div className="flex items-center justify-between mb-1.5 text-xs">
              <span className="text-foreground/75">
                {i + 1}. {s.l}
              </span>
              <span className="font-black text-foreground">
                {s.n} <span className="text-muted-foreground/80 font-normal">({s.v}٪)</span>
              </span>
            </div>
            <div className="h-2.5 rounded-full bg-foreground/[0.05] overflow-hidden">
              <div
                className="h-full bg-gradient-to-l from-primary-main to-secondary-main"
                style={{ width: `${s.v}%` }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default Page;
